import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Input } from '@/Components/ui/input';
import { Textarea } from '@/Components/ui/textarea';
import { Label } from '@/Components/ui/label';
import { Badge } from '@/Components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/Components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/Components/ui/dialog';
import { toast } from 'sonner';
import { API_BASE_URL } from '@/config/api';
import {
  Plus, Pencil, Trash2, Loader2, MessagesSquare, ChevronUp, ChevronDown, ArrowLeft, Upload, AlertTriangle,
} from 'lucide-react';

/**
 * Company P&B Question Builder — create named, reusable Personality &
 * Behavioural question sets. When a recruiter schedules a Realtime P&B
 * interview with a template selected, Sana asks these questions
 * conversationally (as the backbone) with light follow-ups.
 *
 * Backend: /api/pnb/templates CRUD (Web-Interviewer, company-scoped).
 */

const DIMENSIONS = [
  { value: 'behavioural', label: 'Behavioural' },
  { value: 'personality', label: 'Personality' },
  { value: 'general',      label: 'General' },
];

// A 30-min live P&B interview realistically covers ~22 assistant turns
// (questions + follow-ups). We still let recruiters save up to 50, but nudge
// them toward a set that actually fits one interview so the tail isn't skipped.
const RECOMMENDED_QUESTIONS = 15;
const SOFT_WARN_AT = 20;

const emptyQuestion = () => ({ question: '', dimension: 'behavioural' });

const api = (path, opts = {}) =>
  fetch(`${API_BASE_URL}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });

export default function PnbBuilderSection() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);        // null = list view
  const [confirmArchive, setConfirmArchive] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api('/api/pnb/templates');
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      setTemplates(data.templates || []);
    } catch (e) {
      console.error('[PnbBuilder] list failed', e);
      toast.error('Could not load P&B templates.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const startCreate = () =>
    setDraft({ id: null, name: '', questions: [emptyQuestion()] });

  const startEdit = async (id) => {
    try {
      const res = await api(`/api/pnb/templates/${id}`);
      if (!res.ok) throw new Error(`Status ${res.status}`);
      const data = await res.json();
      const t = data.template || {};
      setDraft({
        id: t.id,
        name: t.name || '',
        questions: (t.questions || []).map((q) => ({
          question: q.question || '',
          dimension: q.dimension || 'general',
        })),
      });
    } catch (e) {
      console.error('[PnbBuilder] load template failed', e);
      toast.error('Could not open that template.');
    }
  };

  const archive = async (id) => {
    try {
      const res = await api(`/api/pnb/templates/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      toast.success('Template deleted.');
      setConfirmArchive(null);
      loadTemplates();
    } catch (e) {
      console.error('[PnbBuilder] archive failed', e);
      toast.error('Could not delete that template.');
    }
  };

  // ── draft editing helpers ──────────────────────────────────────────────────
  const setQuestion = (idx, patch) =>
    setDraft((d) => ({
      ...d,
      questions: d.questions.map((q, i) => (i === idx ? { ...q, ...patch } : q)),
    }));
  const addQuestion = () =>
    setDraft((d) => ({ ...d, questions: [...d.questions, emptyQuestion()] }));
  const removeQuestion = (idx) =>
    setDraft((d) => ({ ...d, questions: d.questions.filter((_, i) => i !== idx) }));
  const moveQuestion = (idx, dir) =>
    setDraft((d) => {
      const j = idx + dir;
      if (j < 0 || j >= d.questions.length) return d;
      const qs = [...d.questions];
      [qs[idx], qs[j]] = [qs[j], qs[idx]];
      return { ...d, questions: qs };
    });

  // ── import questions from an uploaded PDF/Word file ────────────────────────
  const onImportClick = () => fileInputRef.current?.click();

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';   // allow re-picking the same file
    if (!file) return;

    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!['pdf', 'docx', 'doc'].includes(ext)) {
      toast.error('Upload a PDF or Word file (.pdf, .docx, .doc).');
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('File too large (max 15 MB).');
      return;
    }

    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // NOTE: no Content-Type header — the browser sets the multipart boundary.
      const res = await fetch(`${API_BASE_URL}/api/pnb/templates/import`, {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 402) {
        toast.error(data.error || 'Importing a question set costs 1 credit — your balance is too low. Please top up.');
        return;
      }
      if (!res.ok) throw new Error(data.error || `Status ${res.status}`);

      const imported = (Array.isArray(data.questions) ? data.questions : [])
        .map((q) => ({
          question: String(q.question || '').trim(),
          dimension: q.dimension || 'general',
        }))
        .filter((q) => q.question);

      if (!imported.length) {
        toast(data.warning || 'No interview questions were found in that file.');
        return;
      }

      setDraft((d) => {
        // From the list view there's no draft yet — importing starts a new one
        // and drops the recruiter straight into the editor to review.
        const base = d || { id: null, name: '', questions: [] };
        const kept = (base.questions || []).filter((q) => (q.question || '').trim());
        const merged = [...kept, ...imported].slice(0, 50);
        return { ...base, questions: merged.length ? merged : [emptyQuestion()] };
      });
      toast.success(
        `Imported ${imported.length} question${imported.length === 1 ? '' : 's'} — review and tag below.`,
      );
    } catch (err) {
      console.error('[PnbBuilder] import failed', err);
      toast.error(err.message || 'Could not import that file.');
    } finally {
      setImporting(false);
    }
  };

  const saveDraft = async () => {
    const name = (draft.name || '').trim();
    if (!name) { toast.error('Give the template a name.'); return; }
    const questions = (draft.questions || [])
      .map((q) => ({ question: (q.question || '').trim(), dimension: q.dimension || 'general' }))
      .filter((q) => q.question);
    if (!questions.length) { toast.error('Add at least one question.'); return; }

    setSaving(true);
    try {
      const isEdit = !!draft.id;
      const res = await api(
        isEdit ? `/api/pnb/templates/${draft.id}` : '/api/pnb/templates',
        { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify({ name, questions }) },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Status ${res.status}`);
      toast.success(isEdit ? 'Template updated.' : 'Template created.');
      setDraft(null);
      loadTemplates();
    } catch (e) {
      console.error('[PnbBuilder] save failed', e);
      toast.error(e.message || 'Could not save the template.');
    } finally {
      setSaving(false);
    }
  };

  // ── editor view ────────────────────────────────────────────────────────────
  if (draft) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setDraft(null)} className="gap-1">
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
          <h2 className="text-lg font-semibold">{draft.id ? 'Edit template' : 'New template'}</h2>
        </div>

        <div className="space-y-1.5 max-w-md">
          <Label htmlFor="pnb-name">Template name</Label>
          <Input
            id="pnb-name"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder="e.g. Senior Engineer — P&B set"
            maxLength={200}
          />
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <Label>Questions ({draft.questions.length})</Label>
            <div className="flex items-center gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                hidden
                onChange={onImportFile}
              />
              <Button size="sm" variant="outline" onClick={onImportClick} disabled={importing} className="gap-1"
                title="Importing a question set costs 1 credit; adding questions manually is free.">
                {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {importing ? 'Importing…' : 'Import from file'}
                {!importing && <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">1 credit</Badge>}
              </Button>
              <Button size="sm" variant="outline" onClick={addQuestion} className="gap-1">
                <Plus className="w-3.5 h-3.5" /> Add question
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground -mt-1">
            Have a question bank? <span className="font-medium">Import from file</span> pulls questions out of a PDF or Word doc and auto-tags each one — review before saving.
            {' '}Importing deducts <span className="font-medium">1 credit</span>; adding questions manually is free.
            {' '}Around <span className="font-medium">{RECOMMENDED_QUESTIONS}</span> questions work best for a 30-minute interview (leaves room for follow-ups).
          </p>

          {draft.questions.length > SOFT_WARN_AT && (
            <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                This set has <span className="font-semibold">{draft.questions.length}</span> questions. A 30-minute
                interview covers about <span className="font-semibold">22</span> turns (questions plus follow-ups), so
                the later questions here may not be reached. Consider trimming to ~{RECOMMENDED_QUESTIONS}, or split
                into more than one template. You can still save this as is.
              </span>
            </div>
          )}

          {draft.questions.map((q, idx) => (
            <Card key={idx} className="border-border/60">
              <CardContent className="p-3 space-y-2">
                <div className="flex items-start gap-2">
                  <span className="text-xs font-medium text-muted-foreground mt-2 w-5 shrink-0">{idx + 1}.</span>
                  <Textarea
                    value={q.question}
                    onChange={(e) => setQuestion(idx, { question: e.target.value })}
                    placeholder="Type the interview question…"
                    rows={2}
                    className="flex-1"
                    maxLength={1000}
                  />
                </div>
                <div className="flex items-center gap-2 pl-7">
                  <Select value={q.dimension} onValueChange={(v) => setQuestion(idx, { dimension: v })}>
                    <SelectTrigger className="h-8 w-40 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {DIMENSIONS.map((d) => (
                        <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="ml-auto flex items-center gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8" disabled={idx === 0}
                      onClick={() => moveQuestion(idx, -1)} aria-label="Move up">
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8" disabled={idx === draft.questions.length - 1}
                      onClick={() => moveQuestion(idx, 1)} aria-label="Move down">
                      <ChevronDown className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive"
                      onClick={() => removeQuestion(idx)} aria-label="Remove">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button onClick={saveDraft} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            {draft.id ? 'Save changes' : 'Create template'}
          </Button>
          <Button variant="outline" onClick={() => setDraft(null)} disabled={saving}>Cancel</Button>
        </div>
      </div>
    );
  }

  // ── list view ──────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">P&amp;B Question Builder</h2>
          <p className="text-sm text-muted-foreground">
            Create reusable Personality &amp; Behavioural question sets. Pick one when scheduling a
            Realtime P&amp;B interview and Sana will ask them conversationally.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
            hidden
            onChange={onImportFile}
          />
          <Button variant="outline" onClick={onImportClick} disabled={importing} className="gap-2"
            title="Importing a question set costs 1 credit; adding questions manually is free.">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {importing ? 'Importing…' : 'Import from file'}
            {!importing && <Badge variant="secondary" className="ml-1 text-[10px] px-1.5 py-0">1 credit</Badge>}
          </Button>
          <Button onClick={startCreate} className="gap-2">
            <Plus className="w-4 h-4" /> New template
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
        </div>
      ) : templates.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <MessagesSquare className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
            <p className="font-medium">No templates yet</p>
            <p className="text-sm text-muted-foreground mt-1 mb-4">Create your first P&amp;B question set.</p>
            <Button onClick={startCreate} className="gap-2"><Plus className="w-4 h-4" /> New template</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {templates.map((t) => (
            <Card key={t.id}>
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{t.name}</div>
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
                    <Badge variant="secondary">
                      {t.question_count} question{Number(t.question_count) === 1 ? '' : 's'}
                    </Badge>
                    <span>Updated {t.updated_at ? new Date(t.updated_at).toLocaleDateString() : '—'}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="outline" size="sm" onClick={() => startEdit(t.id)} className="gap-1">
                    <Pencil className="w-3.5 h-3.5" /> Edit
                  </Button>
                  <Button variant="ghost" size="icon" className="text-destructive"
                    onClick={() => setConfirmArchive(t)} aria-label="Delete">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!confirmArchive} onOpenChange={(o) => !o && setConfirmArchive(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete template?</DialogTitle>
            <DialogDescription>
              “{confirmArchive?.name}” will be removed from the Builder. Interviews already scheduled
              with it are unaffected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmArchive(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => confirmArchive && archive(confirmArchive.id)}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
